<script setup lang="ts">
/**
 * One process in full (server/processes.ts `processInfo`), opened from a row of
 * ProcessBreakdown. Its parent and children are links: following one shows
 * that process here instead, and "back" returns to the row's own.
 *
 * cwd, open files and disk I/O are only readable for todos's own user's
 * processes; for anyone else's they are simply left out.
 */
import { computed, ref, watch } from "vue";
import { api } from "../lib/api.js";
import { bytesLabel } from "../lib/bytes.js";
import type { ProcessInfo } from "@shared/types.js";

const props = defineProps<{ pid: number }>();

const pid = ref(props.pid);
const info = ref<ProcessInfo | null>(null);
const loading = ref(false);
const error = ref("");

let asked = 0;
async function load(): Promise<void> {
  const mine = ++asked;
  loading.value = true;
  try {
    const p = (await api.get<{ process: ProcessInfo }>(`/api/system-stats/processes/${pid.value}`)).process;
    if (mine !== asked) return;
    info.value = p;
    error.value = "";
  } catch (err) {
    if (mine !== asked) return;
    info.value = null;
    error.value = (err as Error).message;
  } finally {
    if (mine === asked) loading.value = false;
  }
}

watch(pid, () => void load(), { immediate: true });

function duration(seconds: number): string {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

const started = computed(() => {
  const p = info.value;
  if (!p) return "";
  const when = new Date(p.startedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  return `${when} (${duration((Date.now() - p.startedAt) / 1000)} ago)`;
});

const rows = computed<[string, string][]>(() => {
  const p = info.value;
  if (!p) return [];
  const out: [string, string][] = [
    ["User", p.user],
    ["State", p.state],
    ["Started", started.value],
    ["CPU now", `${p.cpu < 0.5 ? 0 : Math.round(p.cpu)}% of all cores`],
    ["CPU time", duration(p.cpuSeconds)],
    ["Memory", bytesLabel(p.rssBytes) + (p.peakRssBytes !== null ? ` · peak ${bytesLabel(p.peakRssBytes)}` : "")],
    ["Virtual", bytesLabel(p.virtualBytes)],
    ["Threads", String(p.threads)],
    ["Nice", String(p.nice)],
  ];
  if (p.swapBytes) out.push(["Swap", bytesLabel(p.swapBytes)]);
  if (p.openFiles !== null) out.push(["Open files", String(p.openFiles)]);
  if (p.readBytes !== null && p.writeBytes !== null) out.push(["Disk I/O", `${bytesLabel(p.readBytes)} read · ${bytesLabel(p.writeBytes)} written`]);
  if (p.cwd) out.push(["Directory", p.cwd]);
  return out;
});
</script>

<template>
  <div class="proc-detail" @click.stop>
    <div class="stats-detail-head">
      <strong>{{ info?.name ?? "…" }}</strong>
      <span class="stats-sub">pid {{ pid }}</span>
      <button v-if="pid !== props.pid" class="btn ghost" type="button" @click="pid = props.pid">‹ back to {{ props.pid }}</button>
      <span style="flex: 1"></span>
      <button class="btn ghost" type="button" :disabled="loading" @click="load">{{ loading ? "Reading…" : "Refresh" }}</button>
    </div>
    <p v-if="error" class="field-problem">{{ error }}</p>
    <p v-else-if="!info" class="stats-sub">Reading…</p>
    <template v-if="info">
      <code class="proc-cmd">{{ info.command }}</code>
      <dl class="proc-facts">
        <template v-for="[k, v] in rows" :key="k">
          <dt>{{ k }}</dt>
          <dd>{{ v }}</dd>
        </template>
        <template v-if="info.parent">
          <dt>Parent</dt>
          <dd><button class="proc-link" type="button" @click="pid = info.parent.pid">{{ info.parent.name }} ({{ info.parent.pid }})</button></dd>
        </template>
        <template v-if="info.children.length">
          <dt>Children</dt>
          <dd class="proc-children">
            <button v-for="c in info.children" :key="c.pid" class="proc-link" type="button" @click="pid = c.pid">{{ c.name }} ({{ c.pid }})</button>
          </dd>
        </template>
      </dl>
    </template>
  </div>
</template>
